import * as React from 'react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { observer } from 'src/utils/mobx-react';
import bgPatternHexagon from 'src/domains/layouts/shared/assets/backgrounds/sports/bgPatternHexagon.webp';
import { DateTime } from 'src_common/utils/time/time';
import { PreMatchNFL } from 'src/domains/sportsbook/webview/components/matchLead/PreMatchNFL';
import {
    PreMatchDate,
    PreMatchDateTime,
    PreMatchOutrightsWrapper,
    PreMatchParticipantName,
    PreMatchParticipantNameWrapper,
    PreMatchParticipants,
    PreMatchTime,
    PreMatchWrapper,
    Versus,
} from 'src/domains/sportsbook/webview/components/matchLead/PreMatch.style';
import { ParticipantModelType } from 'src_common/common/websocket2/modelsApi/Event';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { getNewHeaderOptions } from 'src/domains/layouts/webview/components/sports/general/config';
import { useAppStateContext } from 'src/appState/AppState';
import { checkCompetition } from 'src/domains/sportsbook/utils/checkCompetition';
import { NBAEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNBA/EventRowNBA.state';
import { EventRowItemLogo } from 'src/domains/sportsbook/webview/components/eventsList/eventRow6Pack/Participant6Pack.style';
import { isOutrightEventItem } from 'src/domains/sportsbook/utils/isOutrightEvent';

export type TeamNameType = Pick<ParticipantModelType, 'role' | 'name'>;

export const getTeamName = (event: EventModel, type: string): TeamNameType | undefined => {
    for (const team of Object.values(event.participants)) {
        if (team.role === type) {
            return team;
        }
    }
};

interface MatchLeadDefaultPropsType {
    event: EventModel;
}

export const PreMatch = observer('PreMatch', ({ event }: MatchLeadDefaultPropsType) => {
    const {
        unifiedComponentConfig: {
            componentVariants: { sportsbookVariant },
            scoreboard,
            unifiedSportEventsTable,
        },
        languagesState,
    } = useAppStateContext();

    const { sport } = event;

    const startTime = DateTime.from(event.timeSettingsStartTime);

    const home = getTeamName(event, 'home');
    const away = getTeamName(event, 'away');

    const competitionName = event.competition2.getModel()?.name;

    // TODO: [Genius] Iris To remove this check and leave only componentVariants.sportsbookVariant when GLOB-37 is fully live
    const variant = sportsbookVariant;

    const eventDateStart = startTime?.format('ddd Do MMM');
    const eventTimeStart = startTime?.format('HH:mm');

    if (unifiedSportEventsTable === false && sport === 'basketball') {
        if (scoreboard.liveTracker !== null) {
            for (const item of scoreboard.liveTracker) {
                if (item === sport) {
                    return null;
                }
            }
        }
    }

    if (sport === 'icehockey') {
        // temporary hide until new implementation - LT-1795
        return null;
    }

    // temporary fix for jumping prematch golf scoreboard
    // problem with event model timeSettingsTimeline - it frequently change from premach to started under some conditions
    // probably have to be fixed on BE (LT-1413)
    // WARNING: enable premach on golf could cause blinking effect
    // if (sport === 'golf') {
    //     return null;
    // }

    /* NBA and NHL have their own scoreboards */
    if (unifiedSportEventsTable === false && (competitionName === 'US NBA' || competitionName === 'US NHL')) {
        return null;
    }

    if (competitionName === 'NFL Matches' || competitionName === 'US NFL') {
        return <PreMatchNFL event={event} />;
    }

    const bgImage = variant === DefaultComponentVariantType.SECONDARY ? undefined : bgPatternHexagon;

    if (unifiedSportEventsTable) {
        const bgImageBySport = getNewHeaderOptions();
        const eventDateStart = startTime?.format('ddd DD MMM');

        const getRowStateConstructor = (event: EventModel) => {
            if (
                checkCompetition(competitionName ?? '', ['NBA']) === true &&
                event.template.startsWith('two-participant')
            ) {
                return NBAEventRow6PackState;
            }

            return null;
        };

        const rowStateConstructor = getRowStateConstructor(event);
        const rowState = rowStateConstructor ? new rowStateConstructor(event.id2, languagesState) : null;
        const homeLogo = rowState?.getParticipantLogo(home?.name ?? '') ?? null;
        const awayLogo = rowState?.getParticipantLogo(away?.name ?? '') ?? null;

        return (
            <PreMatchWrapper
                bgImage={bgImageBySport[sport]}
                variant={variant}
            >
                {isOutrightEventItem(event) || event.sport === 'golf' ? (
                    <PreMatchOutrightsWrapper>
                        <PreMatchParticipantNameWrapper>
                            <PreMatchParticipantName>{event.name}</PreMatchParticipantName>
                            <PreMatchDate variant={variant}>{`${eventDateStart} ${eventTimeStart}`}</PreMatchDate>
                        </PreMatchParticipantNameWrapper>
                    </PreMatchOutrightsWrapper>
                ) : null}
                {home === undefined && away === undefined ? null : (
                    <PreMatchParticipants>
                        <PreMatchParticipantNameWrapper>
                            {homeLogo !== null ? (
                                <EventRowItemLogo
                                    alt={home?.name ?? ''}
                                    decoding='async'
                                    loading='lazy'
                                    variant={variant}
                                    src={homeLogo}
                                />
                            ) : null}
                            <PreMatchParticipantName>{home === undefined ? '-' : home.name}</PreMatchParticipantName>
                        </PreMatchParticipantNameWrapper>
                        <PreMatchDateTime variant={variant}>
                            <Versus variant={variant} />
                            <PreMatchDate variant={variant}>{`${eventDateStart} ${eventTimeStart}`}</PreMatchDate>
                        </PreMatchDateTime>
                        <PreMatchParticipantNameWrapper>
                            {awayLogo !== null ? (
                                <EventRowItemLogo
                                    alt={away?.name ?? ''}
                                    decoding='async'
                                    loading='lazy'
                                    variant={variant}
                                    src={awayLogo}
                                />
                            ) : null}
                            <PreMatchParticipantName>{away === undefined ? '-' : away.name}</PreMatchParticipantName>
                        </PreMatchParticipantNameWrapper>
                    </PreMatchParticipants>
                )}
            </PreMatchWrapper>
        );
    }

    return (
        <PreMatchWrapper
            bgImage={bgImage}
            variant={variant}
        >
            <PreMatchDate variant={variant}>
                {eventDateStart}
                <PreMatchTime>{eventTimeStart}</PreMatchTime>
            </PreMatchDate>

            {home === undefined && away === undefined ? null : (
                <PreMatchParticipants>
                    <PreMatchParticipantName>{home === undefined ? '-' : home.name}</PreMatchParticipantName>
                    <Versus variant={variant} />
                    <PreMatchParticipantName>{away === undefined ? '-' : away.name}</PreMatchParticipantName>
                </PreMatchParticipants>
            )}
        </PreMatchWrapper>
    );
});
